/// <reference lib="dom" />

/**
 * Youngle Quiz - embeddable client library for the youngle.com landing page.
 *
 * Include via script tag:
 *   <script src="https://quiz-engine.youngle.com/lib/youngle-quiz.js"></script>
 *
 * Then initialise:
 *   const quiz = YoungleQuiz.init({ apiBase: 'https://quiz-engine.youngle.com/api', ... });
 *
 * The library handles the full landing page flow: URL parameter detection,
 * heartbeat polling, result retrieval, and token persistence via localStorage.
 * Zero external dependencies.
 */

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export interface ProductGroup {
  shopify_id: string;
  title: string;
  description: string | null;
  image_url: string | null;
  type: "formula" | "foundation";
  is_best_seller: boolean;
  matched_health_markers: string[];
  recommendation_reason: string;
  focus_area: { id: number; name: string } | null;
}

export interface HealthMarkerSource {
  /**
   * Numeric `health_markers.id` for the marker keyed by this entry. Lets
   * cross-section consumers (e.g. the LP biological-priority bars) bridge
   * `biological_priorities[].contributing_marker_ids` to the localised
   * marker name and Q/A row.
   */
  id: number;
  question: string;
  answer: string;
}

export interface QuizResult {
  token?: string;
  focus_area: { id: number; name: string };
  formulas: ProductGroup[];
  sports: ProductGroup[];
  foundations: ProductGroup[];
  health_marker_sources?: Record<string, HealthMarkerSource>;
}

export type QuizPhase =
  | "idle"
  | "awaiting_email"
  | "polling"
  | "fetching"
  | "ready"
  | "error"
  | "conflict";

export interface QuizError {
  code:
    | "NOT_FOUND"
    | "CONFLICT"
    | "RATE_LIMITED"
    | "NETWORK"
    | "SERVER_ERROR"
    | "BAD_REQUEST";
  message: string;
  status?: number;
}

export interface QuizState {
  phase: QuizPhase;
  result: QuizResult | null;
  token: string | null;
  email: string | null;
  error: QuizError | null;
  pollCount: number;
}

export interface QuizConfig {
  /** Base URL for the quiz engine API, e.g. `"https://quiz-engine.youngle.com/api"`. */
  apiBase: string;
  /** BCP 47 locale tag for translated results, e.g. `"no"`, `"de"`. Default: `"en"`. */
  locale?: string;
  /** Heartbeat poll interval in ms. Default: `3000`. */
  pollInterval?: number;
  /** localStorage key for persisting the access token. Default: `"youngle_result_token"`. */
  tokenStorageKey?: string;
  /** Auto-read URL params and start the flow on init. Default: `true`. */
  autoStart?: boolean;
  /** URL param name for the email credential. Default: `"email"`. */
  emailParam?: string;
  /** URL param name for the token credential. Default: `"token"`. */
  tokenParam?: string;
  /** URL param name for the Typeform response id (LP email-collection flow). Default: `"rid"`. */
  responseIdParam?: string;
  /** Fired on every state transition. */
  onStateChange?: (state: Readonly<QuizState>) => void;
  /** Fired when a result is successfully retrieved. */
  onResult?: (result: QuizResult) => void;
  /** Fired on unrecoverable errors and 409 conflicts. */
  onError?: (error: QuizError) => void;
  /** Fired when a new access token is issued (first email access). */
  onTokenIssued?: (token: string) => void;
  /** Fired on each heartbeat poll tick. */
  onPolling?: (pollCount: number) => void;
  /**
   * Fired when a `?rid=` parameter is detected but no email/token is available.
   * The LP should show an email collection form and call `submitEmail(email)`
   * on the returned quiz instance when the user submits.
   */
  onEmailRequired?: () => void;
}

export interface QuizInstance {
  /** Returns a snapshot of the current state. */
  getState(): QuizState;
  /** Auto-detect credentials from URL params / localStorage and start. */
  start(): void;
  /** Start polling for the given email. */
  startWithEmail(email: string): void;
  /** Fetch the result directly using a known token. */
  startWithToken(token: string): void;
  /**
   * Submit the user's email to claim a result identified by `?rid=` param.
   * Called by the LP after the user fills in the email form.
   */
  submitEmail(email: string): void;
  /** Retry using the stored token (useful after a 409 conflict). */
  retry(): void;
  /** Stop all polling, cancel in-flight requests, release resources. */
  destroy(): void;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------

const DEFAULT_POLL_INTERVAL = 3_000;
const DEFAULT_TOKEN_KEY = "youngle_result_token";
const DEFAULT_EMAIL_PARAM = "email";
const DEFAULT_TOKEN_PARAM = "token";
const DEFAULT_RESPONSE_ID_PARAM = "rid";
const DEFAULT_LOCALE = "en";
const CLAIM_RETRY_INTERVAL = 3_000;
const CLAIM_MAX_RETRIES = 40;

// ---------------------------------------------------------------------------
// Locale-aware error messages
// ---------------------------------------------------------------------------

type ErrorCode = QuizError["code"];

const errorMessages: Record<string, Partial<Record<ErrorCode, string>>> = {
  en: {
    NOT_FOUND: "No result found yet.",
    CONFLICT:
      "A token was already issued for this email. If you have visited before, your token may have been lost.",
    RATE_LIMITED: "Too many requests \u2014 please wait a moment.",
  },
  no: {
    NOT_FOUND: "Ingen resultater funnet enda.",
    CONFLICT:
      "En token har allerede blitt utstedt for denne e-posten. Hvis du har bes\u00F8kt f\u00F8r, kan tokenet ditt ha g\u00E5tt tapt.",
    RATE_LIMITED: "For mange foresp\u00F8rsler \u2014 vennligst vent et \u00F8yeblikk.",
  },
};

function localizedErrorMessage(locale: string, code: ErrorCode, fallback: string): string {
  return errorMessages[locale]?.[code] ?? errorMessages["en"]?.[code] ?? fallback;
}

// ---------------------------------------------------------------------------
// Storage helpers — email is persisted alongside the token so we can detect
// credential mismatches when a different person uses the same browser.
// ---------------------------------------------------------------------------

interface StoredCredential {
  token: string;
  email: string | null;
}

function storeCredential(
  key: string,
  token: string,
  email: string | null
): void {
  try {
    localStorage.setItem(key, JSON.stringify({ token, email }));
  } catch {
    /* localStorage unavailable */
  }
}

function loadCredential(key: string): StoredCredential | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed === "string") {
      return { token: parsed, email: null };
    }
    if (parsed && typeof parsed.token === "string") {
      return parsed as StoredCredential;
    }
    return null;
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// init
// ---------------------------------------------------------------------------

/**
 * Creates and returns a `QuizInstance` that manages the full landing page
 * flow: heartbeat polling, result retrieval, and token persistence.
 */
export function init(config: QuizConfig): QuizInstance {
  const {
    apiBase,
    locale = DEFAULT_LOCALE,
    pollInterval = DEFAULT_POLL_INTERVAL,
    tokenStorageKey = DEFAULT_TOKEN_KEY,
    autoStart = true,
    emailParam = DEFAULT_EMAIL_PARAM,
    tokenParam = DEFAULT_TOKEN_PARAM,
    responseIdParam = DEFAULT_RESPONSE_ID_PARAM,
    onStateChange,
    onResult,
    onError,
    onTokenIssued,
    onPolling,
    onEmailRequired,
  } = config;

  const base = apiBase.replace(/\/+$/, "");

  let state: QuizState = {
    phase: "idle",
    result: null,
    token: null,
    email: null,
    error: null,
    pollCount: 0,
  };

  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let abortController: AbortController | null = null;
  let destroyed = false;
  let responseId: string | null = null;

  // -----------------------------------------------------------------------
  // Internal helpers
  // -----------------------------------------------------------------------

  function setState(patch: Partial<QuizState>): void {
    state = { ...state, ...patch };
    if (!destroyed) onStateChange?.({ ...state });
  }

  function cleanup(): void {
    if (pollTimer) {
      clearInterval(pollTimer);
      pollTimer = null;
    }
    abortController?.abort();
    abortController = null;
  }

  // -----------------------------------------------------------------------
  // Result fetching
  // -----------------------------------------------------------------------

  async function fetchResult(
    credential: { email: string } | { token: string }
  ): Promise<void> {
    setState({ phase: "fetching", error: null });

    abortController = new AbortController();

    try {
      const payload = { ...credential, ...(locale !== "en" ? { locale } : {}) };
      const res = await fetch(`${base}/survey-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");

        if (res.status === 404) {
          const error: QuizError = {
            code: "NOT_FOUND",
            message: localizedErrorMessage(locale, "NOT_FOUND", "No result found yet."),
            status: 404,
          };
          setState({ phase: "error", error });
          onError?.(error);
          return;
        }

        if (res.status === 409) {
          const stored = loadCredential(tokenStorageKey);

          if (stored?.token) {
            setState({ token: stored.token });
            await fetchResult({ token: stored.token });
            return;
          }

          const error: QuizError = {
            code: "CONFLICT",
            message: localizedErrorMessage(
              locale,
              "CONFLICT",
              "A token was already issued for this email. If you have visited before, your token may have been lost.",
            ),
            status: 409,
          };
          setState({ phase: "conflict", error, token: null });
          onError?.(error);
          return;
        }

        if (res.status === 429) {
          const error: QuizError = {
            code: "RATE_LIMITED",
            message: localizedErrorMessage(locale, "RATE_LIMITED", "Too many requests \u2014 please wait a moment."),
            status: 429,
          };
          setState({ phase: "error", error });
          onError?.(error);
          return;
        }

        const error: QuizError = {
          code: "SERVER_ERROR",
          message: text || `HTTP ${res.status}`,
          status: res.status,
        };
        setState({ phase: "error", error });
        onError?.(error);
        return;
      }

      const data = (await res.json()) as QuizResult;

      if (data.token) {
        storeCredential(tokenStorageKey, data.token, state.email);
        setState({ token: data.token });
        onTokenIssued?.(data.token);
      }

      setState({ phase: "ready", result: data });
      onResult?.(data);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;

      const error: QuizError = {
        code: "NETWORK",
        message: err instanceof Error ? err.message : String(err),
      };
      setState({ phase: "error", error });
      onError?.(error);
    }
  }

  // -----------------------------------------------------------------------
  // Heartbeat polling
  // -----------------------------------------------------------------------

  async function pollHeartbeat(email: string): Promise<void> {
    if (destroyed) return;

    try {
      const res = await fetch(
        `${base}/heartbeat?email=${encodeURIComponent(email)}`,
        abortController ? { signal: abortController.signal } : {}
      );

      if (!res.ok) {
        if (res.status === 429) return;
        throw new Error(`Heartbeat failed: HTTP ${res.status}`);
      }

      const data = (await res.json()) as { ready: boolean };
      const count = state.pollCount + 1;
      setState({ pollCount: count });
      onPolling?.(count);

      if (data.ready) {
        cleanup();
        await fetchResult({ email });
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;

      cleanup();
      const error: QuizError = {
        code: "NETWORK",
        message: err instanceof Error ? err.message : String(err),
      };
      setState({ phase: "error", error });
      onError?.(error);
    }
  }

  function startPolling(email: string): void {
    cleanup();

    abortController = new AbortController();
    setState({
      phase: "polling",
      email,
      error: null,
      result: null,
      pollCount: 0,
    });

    pollHeartbeat(email);
    pollTimer = setInterval(() => pollHeartbeat(email), pollInterval);
  }

  // -----------------------------------------------------------------------
  // Claim-result flow (LP email collection)
  // -----------------------------------------------------------------------

  async function claimResult(rid: string, email: string): Promise<void> {
    if (destroyed) return;

    abortController = new AbortController();

    try {
      const payload = {
        response_id: rid,
        email,
        ...(locale !== "en" ? { locale } : {}),
      };
      const res = await fetch(`${base}/claim-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        signal: abortController.signal,
      });

      if (!res.ok) {
        if (res.status === 404) {
          // Not yet processed - retry after a delay
          const count = state.pollCount + 1;
          setState({ phase: "polling", pollCount: count });
          onPolling?.(count);

          if (count >= CLAIM_MAX_RETRIES) {
            cleanup();
            const error: QuizError = {
              code: "NOT_FOUND",
              message: localizedErrorMessage(locale, "NOT_FOUND", "No result found yet."),
              status: 404,
            };
            setState({ phase: "error", error });
            onError?.(error);
            return;
          }

          pollTimer = setTimeout(() => {
            pollTimer = null;
            claimResult(rid, email);
          }, CLAIM_RETRY_INTERVAL) as unknown as ReturnType<typeof setInterval>;
          return;
        }

        const text = await res.text().catch(() => "");

        if (res.status === 409) {
          const stored = loadCredential(tokenStorageKey);
          if (stored?.token) {
            setState({ token: stored.token });
            await fetchResult({ token: stored.token });
            return;
          }

          const error: QuizError = {
            code: "CONFLICT",
            message: localizedErrorMessage(
              locale,
              "CONFLICT",
              "A token was already issued for this email. If you have visited before, your token may have been lost.",
            ),
            status: 409,
          };
          setState({ phase: "conflict", error, token: null });
          onError?.(error);
          return;
        }

        if (res.status === 429) {
          pollTimer = setTimeout(() => {
            pollTimer = null;
            claimResult(rid, email);
          }, CLAIM_RETRY_INTERVAL) as unknown as ReturnType<typeof setInterval>;
          return;
        }

        const error: QuizError = {
          code: "SERVER_ERROR",
          message: text || `HTTP ${res.status}`,
          status: res.status,
        };
        setState({ phase: "error", error });
        onError?.(error);
        return;
      }

      const data = (await res.json()) as QuizResult;

      if (data.token) {
        storeCredential(tokenStorageKey, data.token, email);
        setState({ token: data.token });
        onTokenIssued?.(data.token);
      }

      setState({ phase: "ready", result: data });
      onResult?.(data);
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") return;

      const error: QuizError = {
        code: "NETWORK",
        message: err instanceof Error ? err.message : String(err),
      };
      setState({ phase: "error", error });
      onError?.(error);
    }
  }

  function submitEmail(email: string): void {
    if (!responseId) return;
    cleanup();
    setState({ phase: "fetching", email, error: null, pollCount: 0 });
    claimResult(responseId, email);
  }

  // -----------------------------------------------------------------------
  // Public methods
  // -----------------------------------------------------------------------

  function startWithEmail(email: string): void {
    setState({ email });
    startPolling(email);
  }

  function startWithToken(token: string): void {
    storeCredential(tokenStorageKey, token, state.email);
    setState({ token });
    fetchResult({ token });
  }

  function start(): void {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get(tokenParam);
    const urlEmail = params.get(emailParam);
    const urlRid = params.get(responseIdParam);

    if (urlToken) {
      if (urlEmail) setState({ email: urlEmail });
      startWithToken(urlToken);
      return;
    }

    if (urlRid) {
      responseId = urlRid;
      setState({ phase: "awaiting_email" });
      fetch(`${base}/view-result`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ response_id: urlRid }),
      }).catch(() => {});
      onEmailRequired?.();
      return;
    }

    if (urlEmail) {
      startWithEmail(urlEmail);
      return;
    }

    const stored = loadCredential(tokenStorageKey);
    if (stored?.token) {
      setState({ token: stored.token, email: stored.email });
      fetchResult({ token: stored.token });
      return;
    }
  }

  function retry(): void {
    const token = state.token || loadCredential(tokenStorageKey)?.token;
    if (token) {
      fetchResult({ token });
    } else if (state.email) {
      startPolling(state.email);
    }
  }

  function destroy(): void {
    destroyed = true;
    cleanup();
  }

  // -----------------------------------------------------------------------
  // Auto-start
  // -----------------------------------------------------------------------

  if (autoStart) {
    Promise.resolve().then(() => {
      if (!destroyed) start();
    });
  }

  return {
    getState: () => ({ ...state }),
    start,
    startWithEmail,
    startWithToken,
    submitEmail,
    retry,
    destroy,
  };
}
